import { useCallback } from 'react';

import { api } from '@shared/api';
import { bigifyLoanApplication, LoanApplication } from '@shared/api/@types/loans';
import env from '@shared/config';
import { TimesEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';
import { UserStore } from '@shared/store';
import { Uuid, UuidPrefix } from '@shared/types/uuid';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { DateTime } from 'luxon';

const MOCK = Boolean(env.MOCK_LOANS);

const useFetchLoanApplications = (
  options?: Omit<
    UseQueryOptions<LoanApplication[], unknown, LoanApplication[], (string | undefined)[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { getUniqueIdentifier } = UserStore.useUserStore;

  const fetchLoanApplications = useCallback(async () => {
    if (MOCK) {
      return new Promise<LoanApplication[]>((resolve) =>
        setTimeout(
          () =>
            resolve([
              {
                id: 68,
                loanOfferId: 'aede22a1-7f91-44e5-ba1d-76698d07905e',
                userId: 'usr_AESfouF9gcWutZd2G2EtuP' as unknown as Uuid<UuidPrefix.User>,
                status: 'APPROVED',
                createdAt: DateTime.fromMillis(1750983716161),
                updatedAt: DateTime.fromMillis(1751257718157),
                submittedAt: null,
                repaymentType: 'FIXED_TERM',
                blockearnerApplicationId: null,
                blockearnerStatus: null,
                securityAssetCode: 'BTC',
                securityAmount: Big('0'),
                borrowAssetCode: 'AUD',
                borrowPrincipalAmount: Big('5000000'),
                termValue: 365,
                termUnits: 'day',
                primaryPurpose: 'car',
                repaymentMethod: null,
                employmentStatus: 'full_time',
                relationshipStatus: 'separated',
                numDependents: 3,
                monthlyIncome: Big('234'),
                expectedMonthlyIncome: Big('0'),
                monthlyExpenses: Big('2344'),
                expectedMonthlyExpenses: Big('0'),
                totalExistingCreditLimits: Big('34'),
                expectedTotalExistingCreditLimits: Big('23434'),
                depositAddressUuid: null,
              } satisfies LoanApplication,
            ]),
          1000,
        ),
      );
    }

    const response = await api.endpoints.getLoanApplications();
    return response.data.map(bigifyLoanApplication);
  }, []);

  return useQuery<LoanApplication[], unknown, LoanApplication[], (string | undefined)[]>({
    queryKey: ['loan-applications', getUniqueIdentifier()],
    queryFn: fetchLoanApplications,
    keepPreviousData: true,
    staleTime: TimesEnum.MINUTE * 5,
    ...options,
  });
};

export { useFetchLoanApplications };
